<section>
  <div class="section-heading">
    <div>
      <h4>{{ 'software_override.artifact.title' | translate }}</h4>
      <p>{{ 'software_override.artifact.description' | translate }}</p>
    </div>
    <span class="selection-count">
      {{
        'software_override.count.components'
          | translate: { count: components.length }
      }}
    </span>
  </div>

  <app-effective-software-selection
    class="effective-selection-preview"
    [resolutions]="softwareSelectionResolutions"
    [supportedProjectIds]="componentProjectIds"
    [forLicenseBatch]="forLicenseBatch"
  ></app-effective-software-selection>

  @if (components.length === 0) {
    <div class="empty-state">
      <mat-icon>deployed_code</mat-icon>
      <div>
        <strong>{{
          'software_override.artifact.no_components_title' | translate
        }}</strong>
        <span>{{
          'software_override.artifact.no_components' | translate
        }}</span>
      </div>
    </div>
  } @else {
    <div class="artifact-grid">
      @for (component of components; track component.id) {
        <article class="artifact-option">
          <div class="artifact-option-header">
            <div>
              <strong>{{
                component.componentName ||
                  ('software_override.artifact.software_component' | translate)
              }}</strong>
              @if (component.selectedArtifact) {
                <span class="override-badge">{{
                  'software_override.artifact.override' | translate
                }}</span>
              }
            </div>
            <span>
              {{
                component.description ||
                  ('software_override.artifact.licensed_component' | translate)
              }}
            </span>
          </div>

          @if (getBundleArtifactSources(component); as bundleArtifactSources) {
            <div class="bundle-artifacts">
              <span class="source-label">{{
                'software_override.artifact.from_selected_bundle' | translate
              }}</span>
              @if (bundleArtifactSources.length === 0) {
                <span class="no-bundle-artifact">
                  {{ 'software_override.artifact.not_in_bundle' | translate }}
                </span>
              } @else {
                @for (
                  source of bundleArtifactSources;
                  track source.bundle.id + ':' + source.artifact.id
                ) {
                  <div class="artifact-source">
                    <div>
                      <strong>{{ source.artifact.version }}</strong>
                      <span>{{ source.bundle.name }}</span>
                    </div>
                    @if (source.artifact.artifactMaturity) {
                      <span class="maturity">{{
                        source.artifact.artifactMaturity
                      }}</span>
                    }
                  </div>
                }
              }
            </div>

            @if (component.selectedArtifact; as artifact) {
              <div
                class="selected-artifact"
                [class.overrides-bundle]="bundleArtifactSources.length > 0"
              >
                <div>
                  <span>{{
                    'software_override.artifact.explicit_override' | translate
                  }}</span>
                  @if (isUnresolved(artifact)) {
                    <strong class="unresolved-artifact">{{
                      'software_override.unresolved.label' | translate
                    }}</strong>
                  } @else {
                    <strong>{{ artifact.version }}</strong>
                  }
                </div>
                @if (bundleArtifactSources.length > 0) {
                  <span class="override-notice">
                    {{
                      'software_override.artifact.replaces_bundle' | translate
                    }}
                  </span>
                }
                <div class="artifact-actions">
                  <button
                    type="button"
                    mat-stroked-button
                    [disabled]="saving"
                    (click)="artifactOverrideRequested.emit(component)"
                  >
                    {{
                      'software_override.actions.change_override' | translate
                    }}
                  </button>
                  <button
                    type="button"
                    class="remove-override"
                    [attr.aria-label]="
                      'software_override.actions.remove_artifact_override'
                        | translate
                    "
                    [matTooltip]="
                      'software_override.actions.remove_override' | translate
                    "
                    [disabled]="saving"
                    (click)="artifactCleared.emit(component)"
                  >
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            } @else if (!component.artifactOverrideEditorOpen) {
              <button
                type="button"
                class="add-override"
                mat-stroked-button
                [disabled]="saving"
                (click)="artifactOverrideRequested.emit(component)"
              >
                {{ 'software_override.actions.override_artifact' | translate }}
              </button>
            }
          }

          @if (component.artifactOverrideEditorOpen) {
            <div class="override-editor">
              <app-autocomplete
                [data]="component.artifacts ?? []"
                propertyName="versionAndScopes"
                optionValuePropertyName="id"
                [label]="
                  'software_override.artifact.override_version' | translate
                "
                [allowOnlyListValues]="true"
                [preSelectedValue]="component.selectedArtifact ?? null"
                [disabled]="
                  saving ||
                  !!component.artifactsLoading ||
                  (!!component.artifactsLoaded &&
                    (component.artifacts?.length ?? 0) === 0)
                "
                (optionsRequested)="artifactsRequested.emit(component)"
                (valueSelected)="
                  artifactSelected.emit({
                    component: component,
                    artifact: $event,
                  })
                "
              ></app-autocomplete>

              @if (component.artifactsLoading) {
                <div class="option-state" role="status">
                  <span class="loading-indicator" aria-hidden="true"></span>
                  {{ 'software_override.artifact.loading' | translate }}
                </div>
              } @else if (component.artifactsLoadError) {
                <div class="option-state option-error" role="alert">
                  <span>{{ component.artifactsLoadError }}</span>
                  <app-button
                    [small]="true"
                    [title]="'software_override.actions.retry' | translate"
                    (click)="artifactsRequested.emit(component)"
                  ></app-button>
                </div>
              } @else if (
                component.artifactsLoaded &&
                (component.artifacts?.length ?? 0) === 0
              ) {
                <div class="option-state">
                  {{ 'software_override.artifact.no_compatible' | translate }}
                </div>
              }

              <button
                type="button"
                class="cancel-override"
                [disabled]="saving"
                (click)="artifactOverrideCancelled.emit(component)"
              >
                {{ 'software_override.actions.cancel' | translate }}
              </button>
            </div>
          }
        </article>
      }
    </div>
  }
</section>
